import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Button, Textarea } from '@roger/ui-components';
import { AtSign, Send, SmilePlus } from 'lucide-react';
import type { ChannelMemberOption } from '../../services/api';

import { EmojiPicker } from './EmojiPicker';
import { MOTION } from './chatMotion';
import {
  convertTypedShortcode,
  findShortcodeQuery,
  loadEmojiData,
  replaceRange,
  searchShortcodes,
} from './emojiShortcodes';
import { MentionList } from './MentionList';
import { findMentionQuery } from './mentions';

export interface SendBarProps {
  /** Unique per conversation: the empty-channel action focuses the input by id. */
  inputId: string;
  title: string;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  /** Renders above the text area: EMR-1878 pending chips and the limit notice. */
  aboveInput?: React.ReactNode;
  /** Renders at the start of the bottom row, before send: the attach button. */
  toolbarStart?: React.ReactNode;
  /** EMR-1878: a message may carry files and no text. */
  canSendWhenEmpty?: boolean;
  /** Phone sheet sizing: 44px buttons, 15px text. */
  touch?: boolean;
  /** EMR-2560: the @ button and member list. Absent on the new-conversation screen. */
  mentions?: {
    results: ChannelMemberOption[];
    status: 'loading' | 'ready' | 'error';
    onQueryChange: (query: string | null) => void;
    onPick: (member: ChannelMemberOption) => void;
  };
}

const MAX_QUERY_LENGTH = 50;

interface ShortcodeList {
  query: string;
  start: number;
  rows: { id: string; native: string }[];
}

export function SendBar({
  inputId,
  title,
  value,
  onChange,
  onSend,
  disabled = false,
  aboveInput,
  toolbarStart,
  canSendWhenEmpty = false,
  touch = false,
  mentions,
}: SendBarProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const canSend = (value.trim().length > 0 || canSendWhenEmpty) && !disabled;
  const dataRef = useRef<Awaited<ReturnType<typeof loadEmojiData>> | null>(
    null
  );
  // Where to put the caret after the next value update.
  const pendingCaret = useRef<number | null>(null);
  const queryRef = useRef<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [list, setList] = useState<ShortcodeList | null>(null);
  const [emojiActive, setEmojiActive] = useState(0);
  const listRows = touch ? 4 : 8;

  // The `@query` the list is open for, and which row is highlighted.
  const [active, setActive] = useState<{
    start: number;
    query: string;
  } | null>(null);
  const [highlight, setHighlight] = useState(0);
  // The `@` the user closed. Only another `@` opens the list again.
  const dismissedStart = useRef<number | null>(null);

  const listId = `${inputId}-mentions`;
  const onQueryChange = mentions?.onQueryChange;
  const status = mentions?.status;
  const needle = active?.query.toLowerCase() ?? '';
  // Earlier results stay while a new search runs, so show only rows that match.
  const visible = active
    ? (mentions?.results ?? []).filter(member =>
        member.name.toLowerCase().includes(needle)
      )
    : [];
  const highlighted = Math.min(highlight, Math.max(visible.length - 1, 0));

  // Put the caret after an inserted emoji or mention once the new value is in
  // the DOM.
  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    if (caret === null) return;
    pendingCaret.current = null;
    ref.current?.setSelectionRange(caret, caret);
  }, [value]);

  const closeList = () => {
    queryRef.current = null;
    setList(null);
  };

  const detect = (text: string, caret: number) => {
    const found = dataRef.current ? findShortcodeQuery(text, caret) : null;
    queryRef.current = found?.query ?? null;
    if (!found) {
      setList(null);
      return;
    }
    searchShortcodes(found.query, listRows).then(
      rows => {
        // A slower search for an older query must not replace the current list.
        if (queryRef.current !== found.query) return;
        setList(
          rows.length ? { query: found.query, start: found.start, rows } : null
        );
        setEmojiActive(0);
      },
      error => console.error('Emoji search failed', error)
    );
  };

  const insertAt = (start: number, end: number, emoji: string) => {
    const next = replaceRange(value, start, end, emoji);
    pendingCaret.current = next.caret;
    closeList();
    onChange(next.value);
  };

  const insertFromList = (row: { native: string }) => {
    if (!list) return;
    const caret = ref.current?.selectionStart ?? value.length;
    insertAt(list.start, caret, row.native);
  };

  const close = useCallback(
    (dismiss: boolean) => {
      if (dismiss && active) dismissedStart.current = active.start;
      setActive(null);
      onQueryChange?.(null);
    },
    [active, onQueryChange]
  );

  // A query with a space and no match, once the search answered, ends the list.
  const noMatchAfterSpace =
    active !== null &&
    status === 'ready' &&
    /\s/.test(active.query) &&
    visible.length === 0;
  useEffect(() => {
    if (noMatchAfterSpace) close(true);
  }, [noMatchAfterSpace, close]);

  // An emptied draft (after a send) forgets the last closed @ and any open list.
  useEffect(() => {
    if (value !== '') return;
    dismissedStart.current = null;
    if (active) close(false);
  }, [value, active, close]);

  // An input event may open or update the list.
  const syncList = (text: string, caret: number) => {
    const found = findMentionQuery(text, caret);
    if (!found) {
      dismissedStart.current = null;
      if (active) close(false);
      return;
    }
    if (found.start === dismissedStart.current) return;
    dismissedStart.current = null;
    if (found.query.length > MAX_QUERY_LENGTH) {
      if (active) close(false);
      return;
    }
    if (found.query !== active?.query) setHighlight(0);
    setActive(found);
    onQueryChange?.(found.query);
  };

  // Caret moves and blur only close the list.
  const closeIfCaretLeft = (text: string, caret: number) => {
    if (!active) return;
    const found = findMentionQuery(text, caret);
    if (!found || found.start !== active.start || found.query !== active.query)
      close(false);
  };

  const insertAtCaret = () => {
    const el = ref.current;
    if (!el || !mentions) return;
    el.focus();
    const before = value.slice(0, el.selectionStart);
    const after = value.slice(el.selectionEnd);
    const lead = before !== '' && !/\s$/.test(before) ? ' ' : '';
    const caret = before.length + lead.length + 1;
    pendingCaret.current = caret;
    dismissedStart.current = null;
    onChange(`${before}${lead}@${after}`);
    setHighlight(0);
    setActive({ start: caret - 1, query: '' });
    mentions.onQueryChange('');
  };

  const pick = (member: ChannelMemberOption) => {
    if (!active || !mentions) return;
    const end = active.start + 1 + active.query.length;
    const name = `@${member.name}`;
    const after = value.slice(end);
    const gap = /^\s/.test(after) ? '' : ' ';
    pendingCaret.current = active.start + name.length + 1;
    onChange(`${value.slice(0, active.start)}${name}${gap}${after}`);
    close(true);
    mentions.onPick(member);
  };

  // The emoji list wins when both could show; its keys are handled first.
  const emojiListId = `${inputId}-emoji-list`;
  const comboboxControls = list ? emojiListId : active ? listId : undefined;
  const comboboxActive = list
    ? `${inputId}-emoji-${list.rows[emojiActive]?.id}`
    : active && visible[highlighted]
      ? `${listId}-${visible[highlighted].id}`
      : undefined;

  return (
    <div className='border-t px-2.5 py-2'>
      {aboveInput}
      <div className='relative'>
        {list ? (
          <div
            id={emojiListId}
            role='listbox'
            aria-label='Emoji suggestions'
            className={`absolute bottom-full left-0 right-0 z-10 mb-1 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-sm ${MOTION.fade}`}
          >
            {list.rows.map((row, i) => (
              <div
                key={row.id}
                id={`${inputId}-emoji-${row.id}`}
                role='option'
                aria-selected={i === emojiActive}
                tabIndex={-1}
                // Keep focus in the text area so blur does not close the list first.
                onPointerDown={e => e.preventDefault()}
                onClick={() => insertFromList(row)}
                onKeyDown={() => {}}
                className={`flex cursor-pointer items-center gap-2 px-3 text-sm ${
                  touch ? 'min-h-11' : 'py-1'
                } ${i === emojiActive ? 'bg-accent' : ''}`}
              >
                <span className='text-base'>{row.native}</span>
                <span className='text-muted-foreground'>:{row.id}:</span>
              </div>
            ))}
          </div>
        ) : (
          active &&
          mentions && (
            <MentionList
              id={listId}
              rows={visible}
              status={mentions.status}
              highlighted={highlighted}
              touch={touch}
              onPick={pick}
            />
          )
        )}
        <div
          data-composer-box
          className='rounded-lg border border-input bg-background has-[textarea:focus-visible]:border-primary has-[textarea:focus-visible]:ring-1 has-[textarea:focus-visible]:ring-primary'
        >
          <Textarea
            ref={ref}
            id={inputId}
            rows={1}
            value={value}
            textareaSize={touch ? 'autoGrowLg' : 'autoGrow'}
            placeholder={`Message ${title}`}
            {...(mentions && {
              role: 'combobox',
              'aria-expanded': active !== null || list !== null,
            })}
            aria-autocomplete='list'
            aria-controls={comboboxControls}
            aria-activedescendant={comboboxActive}
            onFocus={() => {
              loadEmojiData().then(
                data => {
                  dataRef.current = data;
                },
                error => console.error('Emoji data failed to load', error)
              );
            }}
            onBlur={() => {
              closeList();
              if (active) close(false);
            }}
            onSelect={e => {
              detect(e.currentTarget.value, e.currentTarget.selectionStart);
              closeIfCaretLeft(
                e.currentTarget.value,
                e.currentTarget.selectionStart
              );
            }}
            onChange={e => {
              const next = e.target.value;
              const caret = e.target.selectionStart;
              const composing = (e.nativeEvent as InputEvent).isComposing;
              const converted =
                dataRef.current && !composing
                  ? convertTypedShortcode(dataRef.current, value, next, caret)
                  : null;
              if (converted) {
                pendingCaret.current = converted.caret;
                closeList();
                onChange(converted.value);
                return;
              }
              onChange(next);
              if (!composing) detect(next, caret);
              if (mentions) syncList(next, caret);
            }}
            onKeyDown={e => {
              if ((e.nativeEvent as KeyboardEvent).isComposing) return;
              if (list) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const step = e.key === 'ArrowDown' ? 1 : -1;
                  setEmojiActive(
                    a => (a + step + list.rows.length) % list.rows.length
                  );
                  return;
                }
                if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
                  e.preventDefault();
                  insertFromList(list.rows[emojiActive]);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  closeList();
                  return;
                }
              } else if (active) {
                // Escape ends the list and never reaches the window or sheet.
                if (e.key === 'Escape') {
                  e.preventDefault();
                  close(true);
                  return;
                }
                if (visible.length > 0) {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    const step = e.key === 'ArrowDown' ? 1 : -1;
                    setHighlight(
                      (highlighted + step + visible.length) % visible.length
                    );
                    return;
                  }
                  if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
                    e.preventDefault();
                    pick(visible[highlighted]);
                    return;
                  }
                } else if (e.key === 'Enter' && !e.shiftKey) {
                  // Wait for the search, so a quick `@Ja` + Enter is not sent
                  // as plain text before the rows arrive.
                  if (mentions?.status === 'loading') {
                    e.preventDefault();
                    return;
                  }
                  close(false);
                }
              }
              if (e.key !== 'Enter' || e.shiftKey) return;
              e.preventDefault();
              if (canSend) onSend();
            }}
          />
          <div className='flex items-center justify-between gap-2 px-1.5 pb-1.5'>
            <div className='flex items-center gap-1'>
              {toolbarStart}
              <EmojiPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                touch={touch}
                onSelect={native => {
                  const el = ref.current;
                  insertAt(
                    el?.selectionStart ?? value.length,
                    el?.selectionEnd ?? value.length,
                    native
                  );
                  setPickerOpen(false);
                }}
                onCloseAutoFocus={e => {
                  e.preventDefault();
                  ref.current?.focus();
                }}
                trigger={
                  <Button
                    type='button'
                    variant='ghost'
                    size={touch ? 'iconLg' : 'iconMd'}
                    aria-label='Add emoji'
                    className='shrink-0'
                  >
                    <SmilePlus className='h-4 w-4' />
                  </Button>
                }
              />
              {mentions && (
                <Button
                  type='button'
                  variant='ghost'
                  size={touch ? 'iconLg' : 'iconMd'}
                  aria-label='Mention someone'
                  disabled={disabled}
                  // The text area keeps focus, so the phone keyboard stays up.
                  onMouseDown={e => e.preventDefault()}
                  onPointerDown={e => e.preventDefault()}
                  onClick={insertAtCaret}
                  className='shrink-0'
                >
                  <AtSign className='h-4 w-4' />
                </Button>
              )}
            </div>
            <Button
              variant={canSend ? 'default' : 'ghost'}
              size={touch ? 'iconLg' : 'iconMd'}
              aria-label='Send'
              title='Enter to send, Shift+Enter for a new line'
              disabled={!canSend}
              onClick={onSend}
              className='shrink-0'
            >
              <Send className='h-4 w-4' />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
